import IconLamp from "@src/assets/icons/lamp.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { openPlanModal } from "@src/components/plan/plan-modal/plan-modal";
import {
	PlanFamily,
	PlanStatus,
	SupportedFeature,
	SupportedPlan,
} from "@src/gql/graphql";
import clsx from "clsx";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { UPGRADE_CARD_DISMISSED_KEY } from "./constants";
import { useAvailableFeatures } from "./feature-gate/use-available-features";
import { useMyPlanSuspense } from "./queries/fetch-plan";

export function UpgradePopup() {
	const [showPopup, setShowPopup] = useState(false);
	const [dismissed, setDismissed] = useState(
		() => sessionStorage.getItem(UPGRADE_CARD_DISMISSED_KEY) === "true",
	);
	const { data: plan } = useMyPlanSuspense();
	const { availableFeatures } = useAvailableFeatures();

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowPopup(true);
		}, 1000);

		return () => clearTimeout(timer);
	}, []);

	const isPaidActive =
		(plan.planFamily === PlanFamily.Pro ||
			plan.planFamily === PlanFamily.Team) &&
		plan.status === PlanStatus.Active;
	const isBeliever = plan.plan === SupportedPlan.Believer;

	if (isPaidActive || isBeliever || dismissed) {
		return null;
	}

	const isInTrial = plan.status === PlanStatus.InTrial;
	const isStarter = plan.planFamily === PlanFamily.Starter;
	const isNotActive =
		plan.status !== PlanStatus.Active && plan.status !== PlanStatus.Cancelled;
	const shouldShow = isStarter || isInTrial || isNotActive;

	const creditsStats = availableFeatures.find(
		(feature) => feature.feature === SupportedFeature.CreditsBudget,
	)?.stats;
	const hasCreditsQuota =
		creditsStats?.quota !== undefined && creditsStats.quota > 0;
	const percentUsed = hasCreditsQuota
		? Math.min(100, (creditsStats.current / creditsStats.quota) * 100)
		: 0;

	const openUpgradeModal = () => openPlanModal({ source: "upgrade_popup" });

	return (
		<>
			{showPopup && shouldShow && (
				<motion.div
					initial={{ y: 10, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: 10, opacity: 0 }}
					transition={{ duration: 0.2, type: "spring", bounce: 0 }}
					className="w-full px-3"
				>
					<div
						className={clsx(
							"h-8 px-2 rounded-lg bg-brand-primary-soft",
							"border border-brand-primary/20",
							"flex items-center justify-between gap-1.5",
						)}
					>
						<p className="text-xs font-bold text-text-primary leading-tight truncate">
							{hasCreditsQuota
								? `${Math.round(percentUsed)}% of credits used`
								: "Unlock premium features"}
						</p>

						<div className="flex items-center gap-1.5 shrink-0">
							<button
								type="button"
								onClick={openUpgradeModal}
								className={clsx(
									"border border-solid border-brand-primary rounded-lg",
									"px-2 py-1 text-xs leading-none min-h-4",
									"inline-flex items-center gap-1",
									"text-text-inverse bg-brand-primary hover:bg-brand-primary-hover",
								)}
							>
								<IconLamp className="w-3 h-3" />
								{isInTrial ? "Choose a Plan" : "Upgrade"}
							</button>
							<button
								type="button"
								onClick={() => {
									sessionStorage.setItem(UPGRADE_CARD_DISMISSED_KEY, "true");
									setDismissed(true);
								}}
								aria-label="Dismiss"
								className={clsx(
									"flex size-5 shrink-0 items-center justify-center",
									"text-text-muted hover:text-text-primary",
								)}
							>
								<IconX className="size-3.5" />
							</button>
						</div>
					</div>
				</motion.div>
			)}
		</>
	);
}
